import { Select, SelectOption } from "@amzn/meridian";
import React, { useState, useEffect } from "react";
import { INADEQUATE_INFORMATION, NO_OBJECT_INVOLVED, OTHER } from "src/constants";
import { createSearchRegExp } from "src/helpers";
import { Field } from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { compareStringsIgnoreCase } from "src/utils/stringUtil";
import Column from "@amzn/meridian/column";
import Toggle from "@amzn/meridian/toggle";
import { AuditField, AuditResult, setFieldConfirmedAnswer } from "src/store/auditSlice";
import { isConfirmedAnswerValid } from "src/components/pages/audit/helpers";

interface FullOptionsListProps {
  auditField: AuditField;
  fieldIndex: number;
  shouldShowError: boolean;
}
const FullOptionList = ({ auditField, fieldIndex, shouldShowError }: FullOptionsListProps) => {
  const dispatch = useAppDispatch();
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const { alias } = useAppSelector((state) => state.user);

  const [searchQuery, setSearchQuery] = useState("");
  const [hasOverrideOptions, setHasOverrideOptions] = useState(false);
  const [useOverrideOptions, setUseOverrideOptions] = useState(false);
  const [pendingAnswer, setPendingAnswer] = useState<string | string[] | null>(null);
  const searchRegExp = createSearchRegExp(searchQuery);

  const field = Object.values(selectedModel.fields).find(
    (label: Field) => label.name === auditField.fieldName,
  );

  const many = field?.many || false;

  useEffect(() => {
    if (field?.overrideOptions && field.overrideOptions.length > 0 && !hasOverrideOptions) {
      setHasOverrideOptions(true);
      setUseOverrideOptions(true);
    }
  }, [field?.overrideOptions, hasOverrideOptions]);

  const fieldOptions = field?.options || [];
  const fieldOverrideOptions = field?.overrideOptions || [];
  let displayOptions = useOverrideOptions ? fieldOverrideOptions : fieldOptions;

  const hasOtherOption = fieldOptions.some((option) => compareStringsIgnoreCase(option, OTHER));
  const hasNoObjectInvolved = fieldOptions.some((option) =>
    compareStringsIgnoreCase(option, NO_OBJECT_INVOLVED),
  );
  const hasInadequateInfo = fieldOptions.some((option) =>
    compareStringsIgnoreCase(option, INADEQUATE_INFORMATION),
  );

  // Use pendingAnswer if available, otherwise fall back to confirmedAnswer
  let value = pendingAnswer !== null ? pendingAnswer : auditField.confirmedAnswer;

  // Handle the case where we're falling back to confirmedAnswer and need to format it
  if (pendingAnswer === null) {
    if (many && typeof auditField.confirmedAnswer === "string") {
      value = auditField.confirmedAnswer.includes("|")
        ? auditField.confirmedAnswer.split("|")
        : [auditField.confirmedAnswer];
    }

    if (many) {
      if (typeof auditField.confirmedAnswer === "string") {
        if (auditField.confirmedAnswer.includes("|")) {
          value = auditField.confirmedAnswer.split("|");
        } else {
          value = [auditField.confirmedAnswer as string];
        }
      }
    } else {
      value = auditField.confirmedAnswer as string;
    }
  }

  return (
    <Column alignmentVertical={"top"}>
      {hasOverrideOptions && (
        <Toggle size="small" checked={useOverrideOptions} onChange={setUseOverrideOptions}>
          {"load site config"}
        </Toggle>
      )}
      <Select
        value={value}
        onChange={(answer) => {
          // Store the answer temporarily, dispatch will happen on blur
          setPendingAnswer(answer);
        }}
        onBlur={() => {
          // Dispatch when dropdown closes if there's a pending answer
          if (pendingAnswer !== null) {
            dispatch(
              setFieldConfirmedAnswer({
                fieldIndex: fieldIndex,
                confirmedAnswer: pendingAnswer,
                alias: alias,
                auditResult: AuditResult.NEITHER,
              }),
            );
            setPendingAnswer(null); // Clear pending answer after dispatch
          }
        }}
        placeholder={"Choose"}
        size="medium"
        width={"200px"}
        searchQuery={searchQuery}
        onSearch={setSearchQuery}
        error={shouldShowError && !isConfirmedAnswerValid(auditField)}
        errorMessage={shouldShowError && !auditField.confirmedAnswer && "please select an answer"}
      >
        {displayOptions
          .filter((option) => searchRegExp.test(option))
          .map((option) => (
            <SelectOption value={option} label={option} />
          ))}
      </Select>
    </Column>
  );
};

export default FullOptionList;
